import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import SelectInput from "../../../framework/SelectInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { formStateHandler } from "../../../utils/utils";
import { transformPayload } from "../ManufacturerConstants";
import classes from "./index.module.css";
import {
  saveManufacturer,
  searchManufacturerGroup,
} from "../../../services/manufacturer-services";

const AddManufacturer = ({
  id,
  onSaveManufacturer = () => {},
  onEscape = () => {},
  manufacturerData = null,
}) => {
  const [formState, setFormState] = useState(null);
  const manufacturerNameRef = useRef("");
  const manufacturerShortNameRef = useRef("");
  const manufacturerGroupRef = useRef("");
  const saveManufacturerRef = useRef();
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback(async (fieldName, data) => {
    if (fieldName === "manufacturerName" || !fieldName) {
      if (!data?.manufacturerName?.trim()) {
        displayToast({
          type: "error",
          title: "Please enter a manufacturer name.",
        });
        return false;
      }
    }
    if (fieldName === "manufacturerGroupId" || !fieldName) {
      if (!data?.manufacturerGroupId) {
        displayToast({
          type: "error",
          title: "Please select a manufacturer group name.",
        });
        manufacturerGroupRef.current?.focus();
        return false;
      }
    }
    return true;
  }, []);
  const updateManufacturerHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({
        ...formState,
      });
      return saveManufacturer(payload).then((r) => {
        if (!r) return;
        onSaveManufacturer(r);
        return displayToast({
          type: "success",
          title: "Manufacturer added successfully.",
        });
      });
    },
    [formState, onSaveManufacturer, validateForm]
  );
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveManufacturerRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    const fetchManufacturerGroups = async () => {
      if (manufacturerData) {
        const payload = {
          filters: [
            {
              fieldName: "name",
              fieldType: { type: "string", operation: "like" },
              fieldValue: "",
            },
          ],
          orderBy: [{ fieldName: "name", orderType: "asc" }],
        };

        try {
          const resp = await searchManufacturerGroup(payload);
          setManufacturerOptions(resp);
        } catch (error) {
          console.error("Error fetching manufacturer groups", error);
        }
      }
    };

    fetchManufacturerGroups();
  }, [manufacturerData]);

  useEffect(() => {
    if (manufacturerData) {
      setFormState({
        ...manufacturerData,
        manufacturerGroupId: manufacturerOptions.find(
          (g) => g.manufacturerGroupId === manufacturerData.manufacturerGroupId
        ),
      });
    }
  }, [manufacturerData, manufacturerOptions]);

  useEffect(() => {
    manufacturerNameRef.current?.focus();
  }, []);
  return (
    <div>
      <h4 className={classes["manufacturer-detail-label"]}>
        Manufacturer Details
      </h4>
      <Grid container spacing={2} sx={{ p: "25px 20px" }}>
        <Grid item xs={6}>
          <TextInput
            id="manufacturer_name"
            ref={manufacturerNameRef}
            name="manufacturerName"
            label="Manufacturer Name"
            value={formState?.manufacturerName}
            placeholder="Enter Manufacturer Name"
            onFocus={() => setCurrentComponent("manufacturer_name")}
            onChange={(e) => {
              handleFormChange("manufacturerName", e.target.value);
            }}
            onBlur={() => validateForm("manufacturerName", formState)}
            maxInputLength={2000}
            required={true}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "manufacturer_name",
                down: "add_manufacturer_save_button",
                left: "manufacturer_name",
                right: "manufacturer_srt_name",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="manufacturer_srt_name"
            ref={manufacturerShortNameRef}
            name="manufacturerShortCode"
            value={formState?.manufacturerShortCode}
            label="Short Code"
            onFocus={() => setCurrentComponent("manufacturer_srt_name")}
            onChange={(e) => {
              handleFormChange("manufacturerShortCode", e.target.value);
            }}
            placeholder="Enter Short Code"
            onBlur={() => validateForm("manufacturerShortCode", formState)}
            maxInputLength={2000}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "manufacturer_name",
                down: "manufacturer_grp_name",
                left: "manufacturer_name",
                right: "manufacturer_grp_name",
              }),
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <SelectInput
            id="manufacturer_grp_name"
            name="Manufacturer Group"
            value={formState?.manufacturerGroupId || ""}
            required={true}
            label="Manufacturer Group"
            ref={manufacturerGroupRef}
            options={manufacturerOptions}
            onChange={(event, option) => {
              handleFormChange("manufacturerGroupId", option);
            }}
            disableClearable={false}
            placeholder="Search Manufacturer Group"
            getOptionLabel={(option) => option?.name || option}
            onFocus={() => setCurrentComponent("manufacturer_grp_name")}
            onInputChange={async (e, value) => {
              if (!value) return handleFormChange("manufacturerGroupId", null);
              const newValue = e.target.value;
              if (newValue?.length >= 2) {
                const payload = {
                  filters: [
                    {
                      fieldName: "name",
                      fieldType: { type: "string", operation: "like" },
                      fieldValue: newValue,
                    },
                  ],
                  orderBy: [{ fieldName: "name", orderType: "asc" }],
                };
                const resp = await searchManufacturerGroup(payload);
                setManufacturerOptions(resp);
              }
              handleFormChange("manufacturerGroupId", newValue);
            }}
            noOptionsText="Search Manufacturer Group"
            markers={JSON.stringify({
              right: "manufacturer_name",
              up: "manufacturer_name",
              down: "manufacturer_grp_name",
              left: "manufacturer_srt_name",
            })}
          />
        </Grid>
      </Grid>
      <div className={classes["add-manufacturer-save-button-wrapper"]}>
        <Button
          id="add_manufacturer_save_button"
          ref={saveManufacturerRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Manufacturer"
          color="violet"
          onClick={updateManufacturerHandler}
          markers={JSON.stringify({
            up: "manufacturer_name",
            left: "manufacturer_srt_name",
            right: "add_manufacturer_save_button",
            down: "add_manufacturer_save_button",
          })}
        />
      </div>
    </div>
  );
};

export default AddManufacturer;
